import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from "../utils/logger";

export interface NoteModeContextValue {
  noteMode: boolean;
  toggleNoteMode: () => void;
  selectedNoteTypes: Record<string, boolean>;
  toggleNoteType: (type: string, value?: boolean) => void;
}

const NoteModeContext = createContext<NoteModeContextValue | null>(null);

export function NoteModeProvider({ children }: { children: React.ReactNode }) {
  const [noteMode, setNoteMode] = useState(false);
  const [selectedNoteTypes, setSelectedNoteTypes] = useState({
    client_summary: false,
    provider_reflection: false
  });
  
  useEffect(() => {
    const savedNoteMode = sessionStorage.getItem('noteMode');
    const savedNoteTypes = sessionStorage.getItem('selectedNoteTypes');
    
    if (savedNoteMode !== null) {
      setNoteMode(savedNoteMode === 'true');
    }
    
    if (savedNoteTypes !== null) {
      try {
        setSelectedNoteTypes(JSON.parse(savedNoteTypes));
      } catch (error) {
        logger.error('Error parsing saved note types:', error);
      }
    }
  }, []);
  
  useEffect(() => {
    sessionStorage.setItem('noteMode', noteMode.toString());
    sessionStorage.setItem('selectedNoteTypes', JSON.stringify(selectedNoteTypes));
  }, [noteMode, selectedNoteTypes]);
  
  const toggleNoteMode = () => {
    setNoteMode(!noteMode);
  };
  
  const toggleNoteType = (type, value) => {
    if (value !== undefined) {
      setSelectedNoteTypes(prev => ({
        ...prev,
        [type]: value
      }));
    } else {
      setSelectedNoteTypes(prev => ({
        ...prev,
        [type]: !prev[type]
      }));
    }
  };
  
  return (
    <NoteModeContext.Provider 
      value={{ 
        noteMode, 
        toggleNoteMode, 
        selectedNoteTypes, 
        toggleNoteType 
      }}
    >
      {children}
    </NoteModeContext.Provider>
  );
}

export function useNoteMode(): NoteModeContextValue {
  const context = useContext(NoteModeContext);
  if (!context) {
    throw new Error('useNoteMode must be used within a NoteModeProvider');
  }
  return context;
}
